/**
 * 작성자 : 박승호
 * 날짜 : 2024.08.13
 * 경로 : 카메라 폰트 위치 컴포넌트
 */

import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import ico1 from '../assets/images/icons/ico-channel-1.svg';
import ico4 from '../assets/images/icons/ico-channel-4.svg';
import ico9 from '../assets/images/icons/ico-channel-9.svg';
import ico16 from '../assets/images/icons/ico-channel-16.svg';

const Root = styled.div`
  .color-pallete {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;

    &.selected {
      .btn-box {
        color: var(--text-reverse) !important;
        background-color: var(--primary) !important;
        &:active {
          color: var(--text-reverse) !important;
          background-color: var(--filled-blue-deep) !important;
        }
      }

      .btn-text {
        font-size: 0.875rem;
        font-weight: bold;
        color: var(--primary) !important;
      }
    }
    &.unselected:hover {
      .btn-box {
        background-color: var(--tonal-deep) !important;
      }
    }

    .btn-box {
      border-radius: 0.25rem;
      padding: 0.45rem 0.725rem;
      background-color: var(--tonal);
      line-height: 0.725rem;

      &.small {
        height: 2rem;
        padding: 0.5rem;
      }
      &.medium {
        height: 2.25rem;
        padding: 0.75rem;
      }
      &.minWidth {
        min-width: 2.25rem;
      }
      &.width40 {
        min-width: 4rem;
      }
      &.width60 {
        min-width: 6rem;
      }
      &.width80 {
        min-width: 8rem;
      }
      .material-symbols-rounded {
        font-size: 1.375rem;
      }

      &.filledIcon {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1;
        }
      }

      .name {
        letter-spacing: normal;
        font-size: 0.875rem;
        width: fit-content;
        word-break: keep-all;
        white-space: nowrap;
      }
      &.play_arrow_reverse {
        .material-symbols-rounded {
          transform: rotate(180deg);
        }
      }
      &.negative {
        color: var(--filled-red);
        background-color: var(--tonal-red);
        .name {
          font-weight: 600;
        }
        &:hover {
          background-color: var(--tonal-deep-red);
        }
        &:active {
          color: var(--filled-red-deep);
        }
      }
      &.darkModeOnly {
        color: var(--zinc-200);
        background-color: var(--zinc-800);
        .name {
          font-weight: 400;
        }
        &.selected,
        &:hover {
          color: var(--blue-100);
          background-color: var(--blue-700);
          &:active {
            color: var(--blue-100);
            background-color: var(--blue-600);
          }
        }
        &.unselected:hover {
          color: var(--zinc-100);
          background-color: var(--zinc-600);
        }
        &.negative {
          color: var(--red-500);
          &:hover {
            color: var(--red-100);
            background-color: var(--red-700);
          }
          &:active {
            color: var(--red-100);
            background-color: var(--red-600);
          }
        }
      }
      &.iconWeight_300 {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 300;
        }
      }
    }

    .btn-text {
      font-size: 0.875rem;
      color: var(--zinc-400);
    }
  }
`;

interface ICameraChannelView {
  camCType: string;
  setState: Dispatch<SetStateAction<any>>;
  className?: string;
  minWidth?: boolean;
  darkModeOnly?: boolean;
  iconWeight?: 400 | 300;
  filledIcon?: boolean;
  width?: 'width40' | 'width60' | 'width80';
}

const CameraChannelView = ({ camCType, setState, className, minWidth, darkModeOnly, iconWeight, filledIcon, width }: ICameraChannelView) => {
  // 화면 분할
  //   const initialGridView = localCctvSettings.camCType || cctvSettings.camCType;
  //   const [gridView, setGridView] = useState(initialGridView);
  const icons = [ico1, ico4, ico9, ico16];
  // 화면 분할 Options 생성
  const gridViewOptions = Array.from({ length: 4 }, (_, index) => {
    const indexKey: number = index + 1;
    const key: string = indexKey.toString();
    const name = (indexKey * indexKey).toString();
    const icon = icons[index];
    return {
      key,
      name,
      icon,
    };
  });

  const setClassName = className ?? '';
  const setMinWidth = minWidth ? 'minWidth' : '';
  const setDarkModeOnly = darkModeOnly ? 'darkModeOnly' : '';
  const setIconWeight = iconWeight ? `iconWeight_${iconWeight}` : '';
  const setFilledIcon = filledIcon ? 'filledIcon' : '';
  const setWidth = width ?? '';

  return (
    <Root>
      <div className='color-pallete'>
        {gridViewOptions?.map((item) => {
          const defaultSelect = camCType || '4';
          const isSelected: boolean = defaultSelect === item.key;
          const sqrt = Math.sqrt(+item.name);
          return (
            <div
              key={item.key}
              role='presentation'
              className={`button ${isSelected ? 'selected' : 'unselected'}`}
              onClick={() => setState && setState((prev: any) => ({ ...prev, camCType: item.key }))}
            >
              <div className={`btn-box ${setClassName} ${setMinWidth} ${setDarkModeOnly} ${setIconWeight} ${setFilledIcon} ${setWidth}`}>
                {item.icon && (
                  <span className='material-symbols-rounded'>
                    <img src={item.icon} alt={item.name} />
                  </span>
                )}
              </div>
              {item.name && <span className='btn-text'>{`${`${sqrt.toString()} * ${sqrt.toString()}`}`}</span>}
            </div>
          );
        })}
      </div>
    </Root>
  );
};

export default React.memo(CameraChannelView);
